import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer
} from 'recharts';
import { Button } from 'react-bootstrap';
import { colors } from '../../brand-colors';

/**
 * Social Traffic Chart - Bar showing Last Month vs This Month
 * For LinkedIn, X (Twitter), YouTube traffic
 * Placeholder until PostHog analytics integration is configured
 */
function SocialTrafficChart({ data, onConnect }) {
  // Check if analytics is connected
  if (!data || !data.connected) {
    return (
      <div style={styles.placeholder}>
        <div style={styles.icon}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={colors.teal}>
            <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <p style={styles.title}>Connect Analytics</p>
        <p style={styles.subtitle}>
          {data?.message || 'Connect your analytics to track social media traffic'}
        </p>
        <Button
          variant="outline-info"
          size="sm"
          onClick={onConnect}
          style={styles.button}
        >
          Configure Integration
        </Button>
      </div>
    );
  }

  // Transform data for chart
  const socialData = data.data || {};
  const isSampleData = data.is_sample === true;
  const period = data.period || 'month';

  // Get period labels for legend
  const periodLabels = {
    day: { last: 'Yesterday', this: 'Today' },
    week: { last: 'Last Week', this: 'This Week' },
    month: { last: 'Last Month', this: 'This Month' }
  };
  const labels = periodLabels[period] || periodLabels.month;

  const chartData = [
    {
      name: 'LinkedIn',
      last_period: socialData.linkedin?.last_period || socialData.linkedin?.last_month || 0,
      this_period: socialData.linkedin?.this_period || socialData.linkedin?.this_month || 0
    },
    {
      name: 'X',
      last_period: socialData.twitter?.last_period || socialData.twitter?.last_month || 0,
      this_period: socialData.twitter?.this_period || socialData.twitter?.this_month || 0
    },
    {
      name: 'YouTube',
      last_period: socialData.youtube?.last_period || socialData.youtube?.last_month || 0,
      this_period: socialData.youtube?.this_period || socialData.youtube?.this_month || 0
    }
  ];

  const allZero = chartData.every(d => d.last_period === 0 && d.this_period === 0);

  if (allZero) {
    return (
      <div style={styles.placeholder}>
        <p>Waiting for data...</p>
        <small>Social traffic will sync shortly</small>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {isSampleData && (
        <div style={styles.sampleBadge}>
          SAMPLE DATA
        </div>
      )}
      <ResponsiveContainer width="100%" height={250}>
      <BarChart
        data={chartData}
        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis
          dataKey="name"
          tick={{ fill: colors.grey, fontSize: 12 }}
        />
        <YAxis
          tick={{ fill: colors.grey, fontSize: 12 }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: '#fff',
            border: '1px solid #e0e0e0',
            color: colors.greyDarkest
          }}
          formatter={(value) => [value.toLocaleString(), '']}
        />
        <Legend />
        <Bar
          dataKey="last_period"
          name={labels.last}
          fill={colors.tealMid}
        />
        <Bar
          dataKey="this_period"
          name={labels.this}
          fill={colors.tealDark}
        />
      </BarChart>
    </ResponsiveContainer>
    </div>
  );
}

const styles = {
  sampleBadge: {
    position: 'absolute',
    top: 5,
    right: 10,
    backgroundColor: '#dc3545',
    color: '#fff',
    padding: '4px 10px',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.5px',
    zIndex: 10
  },
  placeholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: 250,
    color: colors.greyMid,
    textAlign: 'center',
    padding: 20
  },
  icon: {
    marginBottom: 15
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: colors.greyDarkest,
    marginBottom: 5
  },
  subtitle: {
    fontSize: 13,
    color: colors.greyMid,
    marginBottom: 15,
    maxWidth: 280
  },
  button: {
    borderColor: colors.teal,
    color: colors.tealDark
  }
};

export default SocialTrafficChart;
